import {
	CablePurpose,
	ElectricalId,
	cableItemById,
	getCatalogItem,
	isJunctionBox,
} from "@/shared/catalog";
import { projectOnSegment, segmentIntersection, type Pt } from "@/shared/lib/geometry";
import type { Cable, ElectricalGroupPurpose, PlanLayers } from "@/shared/plan";
import { cableRoute } from "@/widgets/plan";

const SCREENING_DISTANCE = 100;

export const routesNear = (first: Pt[], second: Pt[], distance = SCREENING_DISTANCE): boolean => {
	for (let firstIndex = 1; firstIndex < first.length; firstIndex++) {
		const firstStart = first[firstIndex - 1];
		const firstEnd = first[firstIndex];
		for (let secondIndex = 1; secondIndex < second.length; secondIndex++) {
			const secondStart = second[secondIndex - 1];
			const secondEnd = second[secondIndex];
			if (segmentIntersection(firstStart, firstEnd, secondStart, secondEnd)) return true;
			if (
				projectOnSegment(firstStart, secondStart, secondEnd).distance <= distance ||
				projectOnSegment(firstEnd, secondStart, secondEnd).distance <= distance ||
				projectOnSegment(secondStart, firstStart, firstEnd).distance <= distance ||
				projectOnSegment(secondEnd, firstStart, firstEnd).distance <= distance
			)
				return true;
		}
	}
	return false;
};

export const routeWallCrossings = (
	route: Pt[],
	walls: { start: Pt; end: Pt; doors?: { from: number; to: number }[] }[],
): Pt[] => {
	if (route.length < 2) return [];
	const crossings: Pt[] = [];
	for (let index = 1; index < route.length; index++) {
		for (const wall of walls) {
			const hit = segmentIntersection(route[index - 1], route[index], wall.start, wall.end);
			if (!hit) continue;
			if (wall.doors?.some((door) => hit.u >= door.from && hit.u <= door.to)) continue;
			const { point } = hit;
			if (
				[route[0], route[route.length - 1], ...crossings].some(
					(other) => Math.hypot(point.x - other.x, point.y - other.y) < 1,
				)
			)
				continue;
			crossings.push(point);
		}
	}
	return crossings;
};

const lowVoltage = (purpose: CablePurpose) =>
	purpose === CablePurpose.LowVoltage || purpose === CablePurpose.Tv;
const materialOf = (cable: Cable) => (cable.material ? cableItemById(cable.material) : null);

const purposeMatches = (group: ElectricalGroupPurpose, purpose: CablePurpose) => {
	if (group === "network") return purpose === CablePurpose.LowVoltage;
	if (group === "tv") return purpose === CablePurpose.Tv;
	if (group === "lighting") return purpose === CablePurpose.Lighting;
	if (group === "outlets") return purpose === CablePurpose.Power;
	if (group === "dedicated")
		return purpose === CablePurpose.Power || purpose === CablePurpose.ThreePhase;
	return true;
};

export interface WiringWarning {
	message: string;
	cableId?: string;
	relatedCableIds?: string[];
}

export const groupWiringWarnings = (warnings: WiringWarning[]) => {
	const grouped = new Map<string, Set<string>>();
	for (const warning of warnings) {
		const cables = grouped.get(warning.message) ?? new Set<string>();
		if (warning.cableId) cables.add(warning.cableId);
		for (const id of warning.relatedCableIds ?? []) cables.add(id);
		grouped.set(warning.message, cables);
	}
	return [...grouped].map(([message, cables]) => ({ message, cableIds: [...cables] }));
};

export const wiringWarnings = (layers: PlanLayers): WiringWarning[] => {
	const warnings: WiringWarning[] = [];
	const cables = Object.values(layers.wiring.items);
	if (cables.some((cable) => !materialOf(cable)))
		warnings.push({
			message:
				"У части кабелей не указана или не найдена марка: проверить их соседство с другими трассами невозможно.",
		});
	for (const cable of cables) {
		const material = materialOf(cable);
		const group = cable.groupId ? layers.wiring.groups[cable.groupId] : undefined;
		const ends = [layers.electrical.fixtures[cable.from], layers.electrical.fixtures[cable.to]];
		const endIds = ends.map((fixture) => fixture?.ref.item);
		if (material && group && !purposeMatches(group.purpose, material.purpose)) {
			warnings.push({
				cableId: cable.id,
				message: `Марка «${material.name}» не соответствует группе «${group.name}».`,
			});
		}
		if (
			endIds.includes(ElectricalId.SocketThreePhase) &&
			material?.purpose !== CablePurpose.ThreePhase
		) {
			warnings.push({ cableId: cable.id, message: "Проверьте марку кабеля трёхфазной розетки." });
		}
		if (
			endIds.includes(ElectricalId.SocketNet) &&
			material &&
			material.purpose !== CablePurpose.LowVoltage
		) {
			warnings.push({ cableId: cable.id, message: "Марка кабеля не соответствует LAN-розетке." });
		}
		if (
			endIds.includes(ElectricalId.SocketTv) &&
			material &&
			material.purpose !== CablePurpose.Tv
		) {
			warnings.push({ cableId: cable.id, message: "ТВ-розетка предполагает коаксиальный кабель." });
		}
		if (
			material &&
			lowVoltage(material.purpose) &&
			endIds.some(
				(id) =>
					id &&
					[
						ElectricalId.Socket,
						ElectricalId.SocketDouble,
						ElectricalId.SocketTriple,
						ElectricalId.SocketQuadruple,
						ElectricalId.SocketQuintuple,
						ElectricalId.SocketUsb,
						ElectricalId.SocketPower,
						ElectricalId.SocketAc,
						ElectricalId.SocketThreePhase,
						ElectricalId.Switch,
						ElectricalId.SwitchDouble,
						ElectricalId.SwitchTriple,
						ElectricalId.Panel,
					].includes(id as ElectricalId),
			)
		) {
			warnings.push({
				cableId: cable.id,
				message: "Слаботочный кабель соединён с силовым прибором.",
			});
		}
		if (
			material &&
			!lowVoltage(material.purpose) &&
			endIds.includes(ElectricalId.LowVoltagePanel)
		) {
			warnings.push({
				cableId: cable.id,
				message: "Силовой кабель соединён со слаботочным узлом.",
			});
		}
		if (
			material?.purpose === CablePurpose.ThreePhase ||
			endIds.includes(ElectricalId.SocketThreePhase)
		) {
			const panel = group?.panelId ? layers.electrical.fixtures[group.panelId] : undefined;
			if (!panel || panel.ref.item !== ElectricalId.Panel || panel.supplyPhases !== 3) {
				warnings.push({ cableId: cable.id, message: "Трёхфазное питание щитка не подтверждено." });
			}
			if (group && group.purpose !== "dedicated") {
				warnings.push({
					cableId: cable.id,
					message: "Для трёхфазного потребителя проверьте отдельную группу.",
				});
			}
		}
	}
	for (const group of Object.values(layers.wiring.groups)) {
		const members = cables.filter((cable) => cable.groupId === group.id);
		if (
			!members.some(
				(cable) =>
					materialOf(cable)?.purpose === CablePurpose.ThreePhase ||
					[cable.from, cable.to].some(
						(id) => layers.electrical.fixtures[id]?.ref.item === ElectricalId.SocketThreePhase,
					),
			)
		)
			continue;
		const loads = new Set(
			members
				.flatMap((cable) => [cable.from, cable.to])
				.filter((id) => {
					const fixture = layers.electrical.fixtures[id];
					return fixture
						? ![ElectricalId.Panel, ElectricalId.JunctionBox].includes(
								fixture.ref.item as ElectricalId,
							)
						: !!layers.electrical.items[id] &&
								!isJunctionBox(getCatalogItem(layers.electrical.items[id].ref));
				}),
		);
		if (loads.size > 1)
			warnings.push({
				message: `В группе «${group.name}» несколько потребителей; трёхфазному прибору нужна отдельная линия.`,
			});
	}
	for (let firstIndex = 0; firstIndex < cables.length; firstIndex++) {
		const first = cables[firstIndex];
		const firstMaterial = materialOf(first);
		if (!firstMaterial) continue;
		const firstRoute = cableRoute(layers.walls, layers.electrical, first);
		if (!firstRoute) continue;
		for (let secondIndex = firstIndex + 1; secondIndex < cables.length; secondIndex++) {
			const second = cables[secondIndex];
			const secondMaterial = materialOf(second);
			if (
				!secondMaterial ||
				lowVoltage(firstMaterial.purpose) === lowVoltage(secondMaterial.purpose)
			)
				continue;
			const secondRoute = cableRoute(layers.walls, layers.electrical, second);
			if (secondRoute && routesNear(firstRoute, secondRoute)) {
				warnings.push({
					cableId: first.id,
					relatedCableIds: [second.id],
					message:
						"Силовая и слаботочная трассы близки или пересекаются на плане. Проверьте разделение и способ прокладки.",
				});
			}
		}
	}
	return warnings;
};
